import React from "react";
import { Provider } from "react-redux";
import { snapUserDataLayerState } from "tsi-common-react/src/api/datalayer";
import { AccountMenu } from "tsi-common-react/src/apps/authentication/components/AccountMenu";
import { SessionKeepAlive } from "tsi-common-react/src/apps/authentication/components/SessionKeepAlive";
import { registerCascades } from "tsi-common-react/src/apps/common/cascades";
import { initCSRToolbar } from "tsi-common-react/src/apps/csr";
import { isoWebPageURL } from "tsi-common-react/src/models/nominals";
import { UGCListSubBlock } from "tsi-common-react/src/models/ugc";
import { readyStateComplete } from "tsi-common-react/src/utils/events";
import { strToBool } from "tsi-common-react/src/utils/format";
import {
    dynamicPlaceComponent,
    placeComponent,
} from "tsi-common-react/src/utils/react";
import { usePageURL } from "tsi-common-react/src/utils/urls";

import { rehydratingStore, store } from "../store";
// These components aren't lazy loaded because they appear on every page
import { MainMenu } from "./components/MainMenu";
import { getMenuConfiguration } from "./menu";

interface IUGCGridData {
    title: string;
    desc: string;
    posts: UGCListSubBlock[];
    cta: number;
}

interface IUGCCarouselData {
    desc: string;
    posts: UGCListSubBlock[];
}

// Register store cascades
registerCascades(store);

// Load data for main menu from Wagtail JSON
const menuItems = getMenuConfiguration();
if (menuItems) {
    placeComponent(
        '[data-place-react="main-menu-dropdown"]',
        <MainMenu menuItems={menuItems} />,
    );
}

// Render account menu
dynamicPlaceComponent('[data-place-react="auth-login-menu-link"]', async () => {
    return (
        <Provider store={store}>
            <AccountMenu />
        </Provider>
    );
}).catch(console.error);

// Render standalone login forms
dynamicPlaceComponent(
    '[data-place-react="auth-login-standalone"]',
    async (elem) => {
        const { LoginForm } = await import(
            "tsi-common-react/src/apps/authentication/components/LoginForm"
        );
        const nextURL = elem.dataset.continueUrl
            ? isoWebPageURL.wrap(elem.dataset.continueUrl)
            : undefined;
        return <LoginForm nextURL={nextURL} />;
    },
).catch(console.error);

// Render Session Keep-Alive Modal
placeComponent(
    '[data-place-react="session-keep-alive-modal"]',
    <SessionKeepAlive />,
);

// Render Email Signup Form in Footer
dynamicPlaceComponent('[data-place-react="tempur-me-signup"]', async () => {
    const { TempurMeFooterSignup } = await import(
        "./components/TempurMeFooterSignup"
    );
    return <TempurMeFooterSignup />;
}).catch(console.error);

// Render Service Footer
dynamicPlaceComponent('[data-place-react="service-footer"]', async (elem) => {
    const { ServiceFooter } = await import(
        "tsi-common-react/src/apps/common/containers/ServiceFooter"
    );
    const snippets = JSON.parse(elem.dataset.snippets || "null");
    const interval = Number(elem.dataset.interval) || null;
    const loops = Number(elem.dataset.loops) || null;

    const ServiceFooterWrapper = () => {
        const contactUsForm = usePageURL("contact_us_form");
        const home = usePageURL("home");
        return (
            <ServiceFooter
                snippets={snippets.snippets}
                interval={interval}
                loops={loops}
                chatOfflineLink={contactUsForm || home}
            />
        );
    };

    return (
        <Provider store={store}>
            <ServiceFooterWrapper />
        </Provider>
    );
}).catch(console.error);

// Place UGC Grid component
dynamicPlaceComponent('[data-place-react="ugc-grid"]', async (elem) => {
    const { UGCGrid } = await import("tsi-common-react/src/common/UGCGrid");
    const ugcGridData: IUGCGridData = JSON.parse(elem.dataset.posts || "");
    await readyStateComplete;
    return <UGCGrid tiles={ugcGridData.posts} />;
}).catch(console.error);

// Place UGC Carousel component
dynamicPlaceComponent('[data-place-react="ugc-carousel"]', async (elem) => {
    const { UGCCarousel } = await import(
        "tsi-common-react/src/common/UGCCarousel"
    );
    const ugcCarouselData: IUGCCarouselData = JSON.parse(
        elem.dataset.posts || "",
    );
    await readyStateComplete;
    return <UGCCarousel tiles={ugcCarouselData.posts} />;
}).catch(console.error);

// Place Live Chat Links
dynamicPlaceComponent('[data-place-react="chat-link"]', async (elem) => {
    const { ChatLink } = await import(
        "tsi-common-react/src/apps/chat/ChatLink"
    );
    const ChatLinkWrapper = () => {
        const contactUsForm = usePageURL("contact_us_form");
        const home = usePageURL("home");
        return (
            <ChatLink
                className={elem.dataset.chatLinkClass}
                chatOnlineText={elem.dataset.chatOnlineText}
                chatOfflineText={elem.dataset.chatOfflineText}
                chatOfflineLink={
                    elem.dataset.chatOfflineLink
                        ? isoWebPageURL.wrap(elem.dataset.chatOfflineLink)
                        : contactUsForm || home
                }
            />
        );
    };
    return <ChatLinkWrapper />;
}).catch(console.error);

dynamicPlaceComponent('[data-place-react="footer-widgets"]', async (elem) => {
    const { FooterWidgets } = await import(
        "tsi-common-react/src/apps/common/containers/FooterWidgets"
    );
    const FooterWidgetsWrapper = () => {
        const contactUsForm = usePageURL("contact_us_form");
        const home = usePageURL("home");
        return (
            <FooterWidgets
                phoneNumber={elem.dataset.phoneNumber || ""}
                showContactSupportWidget={strToBool(
                    elem.dataset.showContactSupport || "",
                )}
                showNearbyStoresWidget={strToBool(
                    elem.dataset.showNearbyStores || "",
                )}
                chatOfflineLink={
                    elem.dataset.chatOfflineLink
                        ? isoWebPageURL.wrap(elem.dataset.chatOfflineLink)
                        : contactUsForm || home
                }
            />
        );
    };
    return (
        <Provider store={store}>
            <FooterWidgetsWrapper />
        </Provider>
    );
}).catch(console.error);

// Chat Card block
dynamicPlaceComponent('[data-place-react="chat-card"]', async () => {
    const { ChatCard } = await import("./components/ChatCard");
    return <ChatCard />;
}).catch(console.error);

// Render countdown timer
dynamicPlaceComponent('[data-place-react="countdown-timer"]', async (elem) => {
    const { CountdownTimer } = await import(
        "tsi-common-react/src/apps/countdown-timer/container/CountdownTimer"
    );
    const timerData = JSON.parse(elem.dataset.timers || "");
    const timersExpiredPromo = elem.dataset.promo || "";
    return (
        <CountdownTimer
            timerData={timerData}
            timersExpiredPromo={timersExpiredPromo}
        />
    );
}).catch(console.error);

// Render user data layer element
dynamicPlaceComponent('[data-place-react="user-data-layer"]', async (elem) => {
    await readyStateComplete;
    // Pause for 1 second to allow the data layer push scripts to run after readyState is complete
    await (async () => {
        return new Promise((res) => {
            setTimeout(res, 1000);
        });
    })();
    const orderID = elem.dataset.order || "";
    await snapUserDataLayerState(orderID);
    return null;
}).catch(console.error);

// Render the CSR toolbar
initCSRToolbar({
    rehydratingStore,
    store,
}).catch(console.error);
